import usePrevious from 'hooks/usePrevious';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, REMOVE_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { generateRandomId, produce } from 'utility/general';

const useLicenceRegistrationData = ({ formId = -1, siteInfoId = -1 }) => {
    const [openPopup, setOpenPopup] = useState(false);

    const serverTableKey = 'studyArticleLicense';
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const dispatch = useDispatch();
    const [rows, setRows] = useState(remoteData ?? []);
    const previousData = usePrevious(remoteData);
    const [isEdit, setEdit] = useState(false);
    const [dialogData, setDialogData] = useState({});

    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const onDialogDataUpdate = (e, clearFields = []) => {
        const { value, name } = e.target;

        setDialogData(
            produce((draft) => {
                draft[name] = value;
                clearFields?.forEach((item) => {
                    delete draft[item];
                });
            }),
        );
    };

    const [choice] = useState({
        articleControl: [
            { key: 'A', label: 'Article' },
            { key: 'C', label: 'Control' },
        ],
        type: [
            { key: 0, label: 'Complementary medicine' },
            { key: 1, label: 'Dental' },
            { key: 2, label: 'Device' },
            { key: 3, label: 'Diet supplement' },
            { key: 4, label: 'Drug' },
            { key: 5, label: 'Exercise' },
            { key: 6, label: 'Health services' },
            { key: 7, label: 'Herb' },
            { key: 8, label: 'Nursing' },
            { key: 9, label: 'Physiotherapy' },
            { key: 10, label: 'Radiotherapy' },
            { key: 11, label: 'Surgery' },
            { key: 12, label: 'Vaccine' },
            { key: 13, label: 'Others' },
        ],
    });

    const getChoiceLabel = (type, key) => {
        return (
            choice[type]?.find((item) => {
                return item.key === key;
            })?.label ?? 'undefined'
        );
    };

    const defaultFormList = {
        articleControl: {
            value: dialogData.articleControl,
            label: 'Article / Control',
            name: 'articleControl',
            type: 'select',
            operators: choice['articleControl'],
            validators: ['isRequired'],
            disabled: true,
            onChange: onDialogDataUpdate,
        },
        type: {
            value: dialogData.type,
            label: 'Type',
            name: 'type',
            type: 'select',
            operators: choice['type'],
            validators: ['isRequired'],
            disabled: true,
            onChange: onDialogDataUpdate,
        },
        name: {
            value: dialogData.name,
            label: 'Name',
            name: 'name',
            type: 'input',
            validators: ['isRequired'],
            allowAction: false,
            onChange: onDialogDataUpdate,
            maxLength: 255,
        },
        subTitle1: {
            type: 'subTitle',
            name: 'Same indication registration',
        },
        sameHK: {
            value: dialogData.sameHK,
            label: 'Hong Kong',
            name: 'sameHK',
            type: 'boolean',
            isRequired: true,
            onChange: onDialogDataUpdate,
        },
        sameOversea: {
            value: dialogData.sameOversea,
            label: 'In Oversea',
            name: 'sameOversea',
            type: 'boolean',
            isRequired: true,
            onChange: onDialogDataUpdate,
        },
        sameCountry: {
            value: dialogData.sameCountry,
            label: 'Overseas Country',
            name: 'sameCountry',
            type: 'input',
            validators: dialogData.sameOversea === true ? ['isRequired'] : [],
            allowAction: dialogData.sameOversea === true,
            onChange: onDialogDataUpdate,
            maxLength: 255,
        },
        subTitle2: {
            type: 'subTitle',
            name: 'Other indication registration',
        },
        otherHK: {
            value: dialogData.otherHK,
            label: 'Hong Kong',
            name: 'otherHK',
            type: 'boolean',
            isRequired: true,
            onChange: onDialogDataUpdate,
        },
        otherOversea: {
            value: dialogData.otherOversea,
            label: 'In Oversea',
            name: 'otherOversea',
            type: 'boolean',
            isRequired: true,
            onChange: onDialogDataUpdate,
        },
        otherCountry: {
            value: dialogData.otherCountry,
            label: 'Overseas Country',
            name: 'otherCountry',
            type: 'input',
            validators: dialogData.otherOversea === true ? ['isRequired'] : [],
            allowAction: dialogData.otherOversea === true,
            onChange: onDialogDataUpdate,
            maxLength: 255,
        },
    };

    const getLabelName = (key) => {
        return defaultFormList[key]?.label ?? 'undefined';
    };

    const handleDelete = async (row) => {
        // handle local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                id: row.id,
                siteInfoId,
                applicationFormId: formId,
            },
        });
    };

    const handleEditClick = (row) => {
        setDialogData(row);
        setOpenPopup(true);
        setEdit(true);
    };

    const handleRowCreate = async (id) => {
        setDialogData({});
        setOpenPopup(true);
        setEdit(false);
    };

    const onRecordUpdate = async (defaultData) => {
        const data = defaultData ?? dialogData;

        data.id = `temp_${generateRandomId()}`;

        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });

        setOpenPopup(false);
    };

    const onEditSubmit = (indexToUpdate, externalToDataPatch) => {
        // if externalToDataPatch exist, the edit is triggered by the table 11.2.2 instead of 11.2.3

        const row = dialogData;
        let postData = updatePostData ?? [];
        if (!row.otherOversea) {
            row.otherCountry = '';
        }

        if (!row.sameOversea) {
            row.sameCountry = '';
        }
        console.log('c useLicenceRegistrationData.js existData', indexToUpdate, externalToDataPatch, row);

        if (indexToUpdate === undefined) {
            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            // push data to update server
            if (index === -1) {
                postData.push(row);
            } else {
                postData[index] = row;
            }
        } else {
            const existData = rows[indexToUpdate];

            const existDataId = existData.id;

            const index = postData.findIndex((item) => {
                return item.id === existDataId;
            });

            // push data to update server
            if (index === -1) {
                postData.push({ ...existData, ...externalToDataPatch });
            } else {
                if (externalToDataPatch) {
                    postData[index] = { ...existData, ...externalToDataPatch };
                } else {
                    postData[index] = row;
                }
            }
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                if (indexToUpdate !== undefined) {
                    const existingData = rows[indexToUpdate];
                    draft[indexToUpdate] = { ...existingData, ...externalToDataPatch };
                } else {
                    const index = draft.findIndex((item) => {
                        return item.id === row.id;
                    });

                    draft[index] = row;
                }
            }),
        );

        setOpenPopup(false);
    };

    const recoverDataCreation = async (item) => {
        // handle edited data recovery
        const updatedPostData = [];

        rows.forEach((item) => {
            const remoteDataObject = remoteData.find((remoteItem) => {
                return remoteItem.id === item.id;
            });

            if (remoteDataObject) {
                const isSame = Object.entries(item).every(([key, value]) => {
                    // remove exception
                    if (key === 'siteInfo' || key === 'applicationForm') {
                        return true;
                    }

                    return remoteDataObject[key] === value;
                });

                if (!isSame) {
                    updatedPostData.push(item);
                }
            }
        });

        const postData = rows.filter((row) => {
            const isDataCreation = row.id.toString().startsWith('temp_');

            if (!isDataCreation) {
                dispatch({
                    type: REMOVE_DELETE_REQUEST,
                    payload: {
                        type: serverTableKey,
                        id: row.id,
                        applicationFormId: formId,
                    },
                });
            }

            return isDataCreation;
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...postData, ...updatedPostData],
                applicationFormId: formId,
            },
        });
    };

    const onDataEmpty = async (item) => {
        remoteData?.forEach((row) => {
            dispatch({
                type: ADD_DELETE_REQUEST,
                payload: {
                    type: serverTableKey,
                    id: row.id,
                    siteInfoId,
                    applicationFormId: formId,
                },
            });
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [],
                applicationFormId: formId,
            },
        });
    };

    const columns = [
        {
            field: 'articleControl',
            headerName: getLabelName('articleControl'),
            width: 150,
            valueGetter: (params) => {
                return getChoiceLabel('articleControl', params.value);
            },
        },
        {
            field: 'type',
            headerName: getLabelName('type'),
            width: 100,
            valueGetter: (params) => {
                return getChoiceLabel('type', params.value);
            },
        },
        { field: 'name', headerName: getLabelName('name'), width: 100 },
        { field: 'sameHK', headerName: getLabelName('sameHK'), width: 150 },
        { field: 'sameOversea', headerName: getLabelName('sameOversea'), width: 150 },
        { field: 'sameCountry', headerName: getLabelName('sameCountry'), width: 100 },
        { field: 'otherHK', headerName: getLabelName('otherHK'), width: 100 },
        { field: 'otherOversea', headerName: getLabelName('otherOversea'), width: 150 },
        { field: 'otherCountry', headerName: getLabelName('otherCountry'), width: 150 },
    ];

    const updateRemoteData = () => {
        setRows(remoteData);
    };

    return {
        columns,
        rows,
        openPopup,
        setOpenPopup,
        defaultFormList,
        handleRowCreate,
        onRecordUpdate,
        handleEditClick,
        setRows,
        handleDelete,
        onEditSubmit,
        isEdit,
        onDataEmpty,
        recoverDataCreation,
        updateRemoteData,
    };
};

export default useLicenceRegistrationData;
